// Simple localStorage-backed cart. No external deps.
import { useEffect, useState } from "react";

const CART_KEY = "sws_cart_v1";

function read() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function write(items) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event("sws-cart-changed"));
  } catch { /* ignore quota errors */ }
}

export function getCart() {
  return read();
}

export function cartCount() {
  return read().reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
}

export function addToCart(item) {
  const items = read();
  // Items that carry a bundle_slot_key MUST stay as distinct lines even if
  // two slots share the same sync_variant_id (e.g. shopper picks the same
  // youth-tee size on both little-legacy shirt slots). Merging them would
  // collapse the pro-rated prices and break the advertised bundle total.
  const isBundleLine = !!item.bundle_slot_key || !!item.bundle_slug;
  const idx = isBundleLine
    ? -1
    : items.findIndex((i) => Number(i.sync_variant_id) === Number(item.sync_variant_id) && !i.bundle_slot_key);
  if (idx >= 0) {
    items[idx].quantity = Math.min(10, (items[idx].quantity || 1) + (item.quantity || 1));
  } else {
    items.push({ ...item, quantity: Math.max(1, Math.min(10, item.quantity || 1)) });
  }
  write(items);
  return items;
}

export function updateQty(sync_variant_id, quantity, bundle_slot_key = null) {
  const items = read();
  const idx = items.findIndex((i) =>
    Number(i.sync_variant_id) === Number(sync_variant_id) &&
    (bundle_slot_key ? i.bundle_slot_key === bundle_slot_key : !i.bundle_slot_key)
  );
  if (idx < 0) return items;
  if (quantity <= 0) items.splice(idx, 1);
  else items[idx].quantity = Math.min(10, quantity);
  write(items);
  return items;
}

export function swapVariant(old_sync_variant_id, newFields) {
  // Replace the variant in-place while preserving the line's quantity.
  // newFields: { sync_variant_id, variant_id, item_retail_price, product_name, variant_name, image }
  // Bundle lines are NOT eligible for swap-and-merge — leave them alone.
  const items = read();
  const idx = items.findIndex((i) => Number(i.sync_variant_id) === Number(old_sync_variant_id) && !i.bundle_slot_key);
  if (idx < 0) return items;
  // Check if the new variant already exists as another non-bundle line — merge quantities if so
  const existingIdx = items.findIndex((i, k) => k !== idx && !i.bundle_slot_key && Number(i.sync_variant_id) === Number(newFields.sync_variant_id));
  if (existingIdx >= 0) {
    items[existingIdx].quantity = Math.min(10, items[existingIdx].quantity + items[idx].quantity);
    items.splice(idx, 1);
  } else {
    items[idx] = { ...items[idx], ...newFields };
  }
  write(items);
  return items;
}

export function removeFromCart(sync_variant_id, bundle_slot_key = null) {
  return updateQty(sync_variant_id, 0, bundle_slot_key);
}

/**
 * Remove every line that belongs to a given bundle_slug in one shot.
 * Used when the customer wants to clear a bundle instead of editing per-line.
 */
export function removeBundle(bundle_slug) {
  const items = read().filter((i) => i.bundle_slug !== bundle_slug);
  write(items);
  return items;
}

export function clearCart() {
  write([]);
}

/**
 * Heal cart items that are missing price/image/variant info. Some legacy items
 * (from before we started saving item_retail_price on add) come in with
 * price=0 and no image — checkout then shows $0 subtotal. This walks the cart,
 * fetches the product from the backend, and patches the missing fields for
 * each affected item. Idempotent — safe to call on every mount.
 */
export async function hydrateCart(backendUrl) {
  const items = read();
  if (!backendUrl || items.length === 0) return items;

  const productIds = [...new Set(items
    .filter((it) => !Number(it.item_retail_price) || !it.image || !it.variant_name)
    .map((it) => Number(it.product_id))
    .filter(Boolean))];
  if (productIds.length === 0) return items;

  const products = await Promise.all(productIds.map(async (pid) => {
    try {
      const r = await fetch(`${backendUrl}/api/shop/products/${pid}`);
      if (!r.ok) return null;
      return await r.json();
    } catch { return null; }
  }));

  let mutated = false;
  const next = items.map((it) => {
    // Bundle lines carry backend pro-rated prices (and $0 for the free gift) —
    // NEVER let hydrateCart overwrite them.
    if (it.bundle_slot_key || it.bundle_slug) return it;
    if (Number(it.item_retail_price) > 0 && it.image && it.variant_name) return it;
    const prod = products.find((p) => p && Number(p.id) === Number(it.product_id));
    if (!prod) return it;
    const v = (prod.variants || []).find((x) => Number(x.id) === Number(it.sync_variant_id));
    if (!v) return it;
    mutated = true;
    return {
      ...it,
      item_retail_price: Number(it.item_retail_price) > 0 ? it.item_retail_price : Number(v.retail_price || 0),
      variant_id: it.variant_id || v.variant_id,
      product_name: it.product_name || prod.name,
      variant_name: it.variant_name || v.name || "",
      image: it.image || v.preview_url || prod.thumbnail_url || "",
    };
  });
  if (mutated) write(next);
  return next;
}

// Hook — reactive to changes across tabs
export function useCart() {
  const [items, setItems] = useState(read);
  useEffect(() => {
    const refresh = () => setItems(read());
    // Belt-and-suspenders: force one extra read after the component mounts.
    // Guards against the (rare) case where an add-to-cart write completes
    // during React's initial render pass and the useState initializer
    // snapshotted the old array — the customer would see the item "vanish"
    // until a page refresh. This re-sync eliminates that class of bug.
    refresh();
    window.addEventListener("sws-cart-changed", refresh);
    window.addEventListener("storage", refresh);
    // When the tab regains focus, resync from localStorage in case another
    // tab added/removed items while this one was inactive.
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener("sws-cart-changed", refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);
  return {
    items,
    count: items.reduce((s, it) => s + (Number(it.quantity) || 0), 0),
    subtotal: items.reduce((s, it) => s + (Number(it.item_retail_price) || 0) * (Number(it.quantity) || 0), 0),
    add: addToCart,
    update: updateQty,
    swap: swapVariant,
    remove: removeFromCart,
    clear: clearCart,
  };
}
